// src/app/[subdomain]/b/[branchSlug]/page.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useRouter } from "next/navigation";
import { getThemeBranchLogin } from "@/components/themes";
import BranchDashboard from "@/components/themes/default/admin/BranchDashboard";
import BranchStorefront from "@/components/themes/default/store/BranchStorefront";
import { Loader2, Store, LayoutDashboard, ArrowLeft, ShieldCheck } from "lucide-react";

export default function BranchPortalPage() {
  const params = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();

  // 🌟 ১. বুলেটপ্রুফ সাবডোমেন ও ব্রাঞ্চ স্লাগ রেজোলিউশন (হাইড্রেটিং ফিক্স)
  const resolvedSubdomain = useMemo(() => {
    if (params?.subdomain && params.subdomain !== "undefined") {
      return String(params.subdomain);
    }
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const parts = host.split(".");
      if (parts.length > 2 && !["localhost", "127", "www"].includes(parts[0])) {
        return parts[0];
      }
    }
    return "nextlook";
  }, [params?.subdomain]);

  const resolvedBranchSlug = useMemo(() => {
    if (params?.branchSlug && params.branchSlug !== "undefined") {
      return String(params.branchSlug);
    }
    if (typeof window !== "undefined") {
      const match = window.location.pathname.match(/\/b\/([^/?#]+)/);
      if (match && match[1]) {
        return match[1];
      }
    }
    return "";
  }, [params?.branchSlug]);

  const viewParam = searchParams.get("view"); // 'store' | 'portal'

  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [branchData, setBranchData] = useState<any | null>(null);
  const [branchInfo, setBranchInfo] = useState<any | null>(null);
  const [managerUser, setManagerUser] = useState<any | null>(null);
  const [activeThemeKey, setActiveThemeKey] = useState<string>("default");

  // Login Form States
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Products State
  const [products, setProducts] = useState<any[]>([]);

  const storageKey = `branch_auth_${resolvedSubdomain}_${resolvedBranchSlug}`;

  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("localhost") ||
      window.location.hostname === "127.0.0.1");

  // ২. ব্রাঞ্চের পাবলিক ডাটা ফেচ করা
  const fetchBranchPublicData = useCallback(async () => {
    if (!resolvedBranchSlug) return;
    setLoading(true);

    try {
      const publicUrl = isLocal
        ? `http://${resolvedSubdomain}.localhost:8000/api/v1/stores/public/branch/${resolvedBranchSlug}/?subdomain=${resolvedSubdomain}`
        : `/api/v1/stores/public/branch/${resolvedBranchSlug}/?subdomain=${resolvedSubdomain}`;

      const res = await fetch(publicUrl, { headers: { Accept: "application/json" } });
      if (res.ok) {
        const data = await res.json();
        setBranchData(data);
        if (data.branch) setBranchInfo(data.branch);
        if (Array.isArray(data.products)) setProducts(data.products);

        const themeKey =
          data.theme_config?.active_theme?.theme_key ||
          data.theme_config?.active_theme_details?.theme_key ||
          "default";
        setActiveThemeKey(themeKey);
      } else {
        console.warn("Public branch data returned status:", res.status);
      }
    } catch (err) {
      console.error("Branch public data fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [resolvedSubdomain, resolvedBranchSlug, isLocal]);

  useEffect(() => {
    if (resolvedBranchSlug) {
      fetchBranchPublicData();
    }
  }, [fetchBranchPublicData, resolvedBranchSlug]);

  // ৩. লোকালস্টোরেজ থেকে ম্যানেজার সেশন চেক করা
  useEffect(() => {
    if (typeof window !== "undefined" && resolvedBranchSlug) {
      const savedAuth = localStorage.getItem(storageKey);
      if (savedAuth) {
        try {
          const parsed = JSON.parse(savedAuth);
          if (parsed.token && parsed.branch) {
            setIsAuthenticated(true);
            setBranchInfo(parsed.branch);
            setManagerUser(parsed.user);
            const key =
              parsed.branch?.theme_config?.active_theme?.theme_key ||
              parsed.branch?.theme_config?.active_theme_details?.theme_key;
            if (key) setActiveThemeKey(key);
          }
        } catch {
          localStorage.removeItem(storageKey);
          setIsAuthenticated(false);
        }
      }
    }
  }, [storageKey, resolvedBranchSlug]);

  // ৪. ম্যানেজার লগইন হ্যান্ডলার
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);

    try {
      const apiUrl = isLocal
        ? `http://${resolvedSubdomain}.localhost:8000/api/v1/stores/branches/login/`
        : `/api/v1/stores/branches/login/`;

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password: password,
          branch_slug: resolvedBranchSlug,
          subdomain: resolvedSubdomain,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "লগইন ব্যর্থ হয়েছে।");

      localStorage.setItem(storageKey, JSON.stringify(data));
      setIsAuthenticated(true);
      setBranchInfo(data.branch);
      setManagerUser(data.user);

      const key =
        data.branch?.theme_config?.active_theme?.theme_key ||
        data.branch?.theme_config?.active_theme_details?.theme_key;
      if (key) setActiveThemeKey(key);

      router.push(`/b/${resolvedBranchSlug}?view=portal`);
    } catch (err: any) {
      setLoginError(err.message || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(storageKey);
    setIsAuthenticated(false);
    setManagerUser(null);
    setPassword("");
    router.push(`/b/${resolvedBranchSlug}?view=store`);
  };

  // ৫. ব্রাঞ্চ প্রোডাক্টস রিফ্রেশ
  const fetchProducts = useCallback(async () => {
    if (!branchInfo?.id) return;
    try {
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const prodUrl = isLocal
        ? `http://${resolvedSubdomain}.localhost:8000/api/v1/products/?branch_id=${branchInfo.id}`
        : `/api/v1/products/?branch_id=${branchInfo.id}`;

      const res = await fetch(prodUrl, {
        headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });

      if (res.ok) {
        const data = await res.json();
        const prodList = Array.isArray(data) ? data : data.results || [];
        setProducts(prodList);
        setBranchData((prev: any) => (prev ? { ...prev, products: prodList } : prev));
      }
    } catch (err) {
      console.error("Products refresh error:", err);
    }
  }, [branchInfo?.id, isLocal, storageKey, resolvedSubdomain]);

  // 🌟 ৬. সেফটি গার্ড: branchData নাল হলেও branchInfo দিয়ে অটো-ফলব্যাক গঠন করা
  const effectiveBranchData = useMemo(() => {
    if (branchData && branchData.branch) {
      return branchData;
    }
    if (branchInfo) {
      return {
        branch: branchInfo,
        theme_config: branchInfo?.theme_config || {},
        categories: [],
        products: products || [],
        featured_products: [],
        bestseller_products: [],
        deals_of_the_day: [],
      };
    }
    return null;
  }, [branchData, branchInfo, products]);

  // ৭. লোডিং স্টেট
  if (loading && !effectiveBranchData) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mb-2.5" />
        <p className="text-xs font-mono font-bold text-slate-400">Loading branch outlet...</p>
      </div>
    );
  }

  // ৮. নট-ফাউন্ড স্টেট (ডাটা না থাকলে ক্র্যাশ না করে ক্লিন মেসেজ দেখাবে)
  if (!effectiveBranchData || !effectiveBranchData.branch) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 text-center text-white">
        <Store className="w-12 h-12 text-rose-500 mb-3" />
        <h2 className="text-lg font-black">Branch Outlet Not Found</h2>
        <p className="text-xs text-slate-400 mt-1 max-w-sm">
          Ei branch dokanti khuje paoa jayni ba active nei. Domain ebong slug check korun.
        </p>
      </div>
    );
  }

  // ৯. ভিউ রেজোলিউশন
  const showDashboard = isAuthenticated && viewParam !== "store";
  const showLogin = !isAuthenticated && viewParam === "portal";

  // ১০. ব্রাঞ্চ ম্যানেজার লগইন ভিউ
  if (showLogin) {
    const ActiveThemeLoginComponent = getThemeBranchLogin(activeThemeKey);
    return (
      <div className="relative min-h-screen">
        <button
          onClick={() => router.push(`/b/${resolvedBranchSlug}?view=store`)}
          className="fixed top-4 left-4 z-50 flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-900/80 backdrop-blur-md border border-slate-800 text-slate-300 text-xs font-bold hover:text-white transition-all cursor-pointer shadow-lg"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Outlet Store</span>
        </button>

        <ActiveThemeLoginComponent
          branchSlug={resolvedBranchSlug}
          branchName={effectiveBranchData.branch?.name}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          loading={loginLoading}
          error={loginError}
          onSubmit={handleLogin}
          primaryColor={effectiveBranchData.branch?.theme_config?.primary_color || "#059669"}
        />
      </div>
    );
  }

  // ১১. ব্রাঞ্চ ম্যানেজার ড্যাশবোর্ড ভিউ
  if (showDashboard) {
    return (
      <div className="relative">
        <BranchDashboard
          branchInfo={effectiveBranchData.branch}
          managerUser={managerUser}
          subdomain={resolvedSubdomain}
          branchSlug={resolvedBranchSlug}
          onLogout={handleLogout}
          products={products}
          onRefreshProducts={fetchProducts}
        />
      </div>
    );
  }

  // ১২. ব্রাঞ্চ কাস্টমার স্টোরফ্রন্ট ভিউ (শতভাগ নিরাপদ ডাটা দিয়ে রেন্ডার)
  return (
    <div className="relative min-h-screen">
      {isAuthenticated && (
        <aside
          aria-label="Branch Manager Controls"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 p-2 px-3 rounded-2xl bg-slate-950/90 text-white border border-slate-800 shadow-2xl backdrop-blur-md animate-fadeIn"
        >
          <div className="flex items-center gap-1.5 text-xs font-bold pr-2 border-r border-slate-800">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="hidden sm:inline text-slate-300">Manager Mode</span>
          </div>
          <button
            onClick={() => router.push(`/b/${resolvedBranchSlug}?view=portal`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black shadow-md cursor-pointer transition-all active:scale-95"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span>Branch Dashboard</span>
          </button>
        </aside>
      )}

      {/* 🌟 নিশ্চিত করা হয়েছে যে effectiveBranchData কখনোই null হবে না */}
      <BranchStorefront branchData={effectiveBranchData} subdomain={resolvedSubdomain} />
    </div>
  );
}